import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ComponentServiceBundle } from '@app/core/services/base/component-service-bundle.service';
import { BaseWithBundleComponent } from '@app/shared/components/base/base-with-bundle.component';
import { TemplateService } from '@app/core/services/template.service';
import {
    CategoryTemplateList,
    Template,
    TemplateCategories,
    TemplateVersion
} from '@app/shared/models/distribution-doc-models';
import { FileType, FileTypes } from '@app/shared/models/file-type';
import { FileUtilityService } from '@app/core/services/file-utility.service';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { TextAreaDialogComponent } from '@app/shared/dialogs/text-area-dialog/text-area-dialog.component';
import { ConfirmModalComponent } from '@app/shared/dialogs/confirm-dialog/confirm-modal.component';
import { OP } from '@app/core/services/base/base-service-with-bundle.service';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { SmpUtils } from '@app/core/helpers/utilities';
import { TranslationEntry } from '@app/shared/models/translatable';


@Component({
    selector: 'template-versions',
    templateUrl: './template.component.html',
    styleUrls: ['./template.component.scss']
})
export class TemplateComponent extends BaseWithBundleComponent
                               implements OnInit {

    @Input()
    public template: Template;

    @Input()
    public categoryTemplateList: CategoryTemplateList;

    @Input()
    public caseFileCategories: TranslationEntry[];

    @Output()
    public categoryTemplateListChange = new EventEmitter<CategoryTemplateList>();

    public acceptedFilesTypes: FileType[];
    public showVersions: boolean;

    constructor(protected componentServiceBundle: ComponentServiceBundle,
                protected templateService: TemplateService,
                protected fileUtilityService: FileUtilityService) {
        super(componentServiceBundle);
    }

    public ngOnInit() {
        super.ngOnInit();
        this.acceptedFilesTypes = FileTypes.TYPES_TEMPLATE;
        this.showVersions = false;
    }

    public patchValue(path, value): void {
        const opData = [{
            op: OP.replace,
            path: path,
            value: value
        }];

        this.templateService.patchTemplate(this.categoryTemplateList.name, this.template, opData)
            .subscribe(() => {
                this.template[path] = opData[0].value;
            }, err => this.handleError(err));
    }

    public downloadLatestTemplateVersion(): void {
        this.templateService.getTemplateContent(this.template)
            .subscribe((blob: Blob) => {
                const activeVersion = this.template.activeVersion;
                const fileExt = SmpUtils.getExtensionFromFileName(activeVersion.filename);
                const fileType = FileTypes.getByKey(fileExt);
                this.fileUtilityService.downloadBlob(blob, fileType.mimeType, activeVersion.filename);
            });
    }

    public uploadNewTemplateVersion(files: File[]): void {
        if (files && files.length > 0 && this.template) {
            this.createTemplateVersion(files[0]);
        }
    }

    public deleteTemplate(): void {

        const dialogRef = this.createDeleteDialogRef(this.template.display_name);

        dialogRef.afterClosed()
            .subscribe((result: boolean) => {
                if (result) {
                    this.templateService.deleteTemplate(this.template)
                        .subscribe(res => {
                            this.categoryTemplateList.removeItem(this.template);
                            this.categoryTemplateListChange.emit(this.categoryTemplateList);
                        }, err => this.handleError(err));
                }
            });
    }

    public createTemplateVersion(file: File): void {
        const newTemplateVersion = new TemplateVersion();
        newTemplateVersion.filename = file.name;
        newTemplateVersion.template_category = TemplateCategories.getByKey(this.categoryTemplateList.name);

        const dialogRef = this.dialog.open(TextAreaDialogComponent, {
            width: '400px',
            data: {
                title: `${this.translateService.instant(marker('common.dialog.upload.title'), { fileName: newTemplateVersion.filename })}`,
                confirmBtnOverride: marker('common.btn.upload'),
                closeBtnOverride: marker('common.btn.cancel'),
                label: newTemplateVersion.trans.description
            }
        });

        dialogRef.afterClosed()
            .subscribe((modalResult: { text: string }) => {
                if (modalResult) {
                    if (modalResult && modalResult.text) {
                        newTemplateVersion.description = modalResult.text;
                    }

                    this.fileUtilityService.fileAsBinaryString(file)
                        .subscribe((fileAsBinaryString) => {

                            this.templateService.addTemplateVersion(fileAsBinaryString, this.template, newTemplateVersion)
                                .subscribe((result: TemplateVersion) => {
                                    this.template.versions.unshift(result);
                                }, err => {
                                    this.handleError(err);
                                });

                        });
                }
            });
    }

    public toggleHistoryPanel(): void {
        this.showVersions = !this.showVersions;
    }

    public restoreVersion(version: TemplateVersion): void {
        this.templateService.restoreTemplateVersion(version)
                            .subscribe((result) => {
            this.template.versions.unshift(result);
        }, err => this.handleError(err));
    }

    public deleteVersion(version: TemplateVersion): void {
        const dialogRef = this.createDeleteDialogRef(`${this.template.display_name} v${version.version}`);

        dialogRef.afterClosed()
            .subscribe((result: boolean) => {
                if (result) {
                    this.templateService.deleteTemplateVersion(version)
                        .subscribe(() => {
                            this.template.removeVersion(version);
                        }, err => this.handleError(err));
                }
            });
    }

    private createDeleteDialogRef(deleteName: string): MatDialogRef<any> {
        return this.dialog.open(ConfirmModalComponent, {
            width: '400px',
            data: {
                title: this.translateService.instant(marker('common.dialog.delete.title')),
                text: `${this.translateService.instant(marker('common.dialog.delete.text'), { name: deleteName })}`
            }
        });
    }
}
